import { gql } from '@apollo/client';

export const ASSETS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query assetsByNameAndCountry($search: String!, $country: ID, $feature: String) {
    assetsByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      legacyId
      name
      displayName
      geom
      boundingBox
      hydrocarbonType
      country {
        id
        displayName
        isoCode
        name
      }
    }
  }
`;

export const MAP_SCREENING_QUERY = gql`
  query mapScreening(
    $country: [ID!]
    $company: [ID!]
    $basin: [ID!]
    $shoreStatus: [String!]
    $developmentStatus: [ID!]
    $hydrocarbonType: [String!]
    $prodMin: Float
    $prodMax: Float
    $npvMin: Float
    $npvMax: Float
    $operator: Boolean
    $reservesMin: Float
    $reservesMax: Float
    $opportunities: Boolean
    $includeParticipations: Boolean!
  ) {
    mapScreening(
      country: $country
      company: $company
      basin: $basin
      shoreStatus: $shoreStatus
      developmentStatus: $developmentStatus
      hydrocarbonType: $hydrocarbonType
      prodMin: $prodMin
      prodMax: $prodMax
      npvMin: $npvMin
      npvMax: $npvMax
      operator: $operator
      reservesMin: $reservesMin
      reservesMax: $reservesMax
      opportunities: $opportunities
    ) {
      id
      legacyId
      displayName
      entityType
      name
      geom
      country {
        id
        isoCode
        name
      }
      multiPolygon
      sectionGeomCoordinates
      currentParticipations @include(if: $includeParticipations) {
        effectiveDate
        company {
          id
        }
      }
    }
  }
`;

export const MAP_SCREENING_EA_WELLS_QUERY = gql`
  query mapScreeningEaWells(
    $countryIsoCodes: [String!]
    $companyNames: [String!]
    $basinNames: [String!]
    $shoreStatusNames: [String!]
    $hydrocarbonType: [String!]
    $prodMin: Float
    $prodMax: Float
    $npvMin: Float
    $npvMax: Float
    $operator: Boolean
    $reservesMin: Float
    $reservesMax: Float
    $eaWells: Boolean
  ) {
    mapScreeningEaWells(
      countryIsoCodes: $countryIsoCodes
      companyNames: $companyNames
      basinNames: $basinNames
      shoreStatusNames: $shoreStatusNames
      hydrocarbonType: $hydrocarbonType
      prodMin: $prodMin
      prodMax: $prodMax
      npvMin: $npvMin
      npvMax: $npvMax
      operator: $operator
      reservesMin: $reservesMin
      reservesMax: $reservesMax
      showEaWells: $eaWells
    ) {
      id
      name
      geom
      entityType
      country {
        id
        isoCode
        name
      }
    }
  }
`;

export const BASINS_WITH_MAP_FILTER_QUERY = gql`
  query basinsWithMapFilter($countries: [String!], $companies: [String!], $operator: Boolean) {
    basinsWithMapFilter(countries: $countries, companies: $companies, operator: $operator) {
      id
      name
    }
  }
`;

export const BLOCKS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query blocksByNameAndCountry($search: String!, $country: ID) {
    blocksByNameAndCountry(search: $search, country: $country) {
      id
      leaseLegacyId
      legacyId
      displayName
      entityType
      name
      geom
      country {
        id
        displayName
        isoCode
      }
    }
  }
`;

export const BLOCKS_AND_LEASES_BY_NAME_AND_COUNTRY_QUERY = gql`
  query blocksAndLeasesByNameAndCountry($search: String!, $country: ID, $feature: String) {
    blocksAndLeasesByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      leaseLegacyId
      legacyId
      displayName
      entityType
      name
      geom
      country {
        id
        displayName
        isoCode
      }
    }
  }
`;

export const COMPANIES_WITH_MAP_FILTER_QUERY = gql`
  query companiesWithMapFilter($countries: [String!], $basins: [String!], $operator: Boolean, $search: String, $pagination: PaginationInput) {
    companiesWithMapFilter(countries: $countries, basins: $basins, operator: $operator, search: $search, pagination: $pagination) {
      companies {
        id
        name
      }
      totalCount
    }
  }
`;

export const FACILITIES_BY_NAME_AND_COUNTRY_QUERY = gql`
  query facilitiesByNameAndCountry($search: String!, $country: ID, $feature: String) {
    facilitiesByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      legacyId
      name
      geom
      country {
        id
        displayName
        isoCode
      }
      facilityIcon {
        name
      }
    }
  }
`;

export const FIELDS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query fieldsByNameAndCountry($search: String!, $country: ID, $feature: String) {
    fieldsByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      legacyId
      displayName
      name
      geom
      hydrocarbonType
      country {
        id
        displayName
        isoCode
      }
    }
  }
`;

export const LITHOLOGIES_QUERY = gql`
  {
    lithologies {
      id
      name
      displayName
    }
  }
`;

export const LNG_PROJECTS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query lngProjectsByNameAndCountry($search: String!, $country: String, $feature: String) {
    lngProjectsByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      category
      name
      geom
      lngType
      iconName
      country {
        id
        displayName
        isoCode
      }
    }
  }
`;

export const PIPELINE_NETWORKS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query pipelineNetworksByNameAndCountry($search: String!, $country: ID, $feature: String) {
    pipelineNetworksByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      name
      geom
      countries {
        id
        isoCode
      }
    }
  }
`;

export const SHORE_STATUS_QUERY = gql`
  {
    shoreStatus {
      id
      name
    }
  }
`;

export const WELLS_BY_NAME_AND_COUNTRY_QUERY = gql`
  query wellsByNameAndCountry($search: String!, $country: ID, $feature: String) {
    wellsByNameAndCountry(search: $search, country: $country, feature: $feature) {
      id
      legacyId
      displayName
      name
      geom
      weaClassification
      asset {
        id
      }
      country {
        id
        displayName
        isoCode
      }
    }
  }
`;

export const DEVELOPMENT_STATUS_QUERY = gql`
  {
    developmentStatus {
      id
      name
    }
  }
`;
